import { useEffect, useRef } from "react";
import { Location } from "react-router-dom";
import { logger } from "@amzn/afxtaco-utilities";
import { MessageAttachment } from "@/types";
import { resetExecution } from "../../../../redux/reducers/Execution";
import { AppDispatch } from "../../../../redux/store";
import { detectPageReload } from "../utils/pageReload";
import { ChatStreamingRefs } from "./useChatStreamingRefs";

/**
 * Navigation state passed from HomePage when the user kicks off a new
 * project. We snapshot it once per project boundary because React Router
 * v7's `setSearchParams` resets `location.state` to null, which would
 * otherwise cause downstream effects to misbehave whenever e.g. the
 * version dropdown changes.
 */
export type ChatNavState = {
  isNewProject?: boolean;
  description?: string;
  sessionId?: string;
  attachments?: MessageAttachment[];
} | null;

interface UseProjectBoundaryResetOptions {
  projectId: string | undefined;
  location: Location;
  dispatch: AppDispatch;
  clearCurrentConversation: () => void;
  refs: ChatStreamingRefs;
}

/**
 * Watches the `projectId` route param and, on every project boundary
 * transition (including first mount), snapshots `location.state` into a
 * stable ref and performs a full reset of the chat panel's local +
 * Redux execution state.
 *
 * Returns the captured navigation-state ref so other hooks can read
 * `isNewProject`, `description`, `sessionId` reliably across renders.
 */
export function useProjectBoundaryReset({
  projectId,
  location,
  dispatch,
  clearCurrentConversation,
  refs,
}: UseProjectBoundaryResetOptions): React.MutableRefObject<ChatNavState> {
  const navStateRef = useRef<ChatNavState>(location.state ?? null);
  const prevProjectIdRef = useRef<string | undefined>(undefined);

  // Detect if this page load was a browser refresh
  const isPageReloadRef = useRef<boolean | null>(null);
  if (isPageReloadRef.current === null) {
    isPageReloadRef.current = detectPageReload();
  }

  useEffect(() => {
    const prevProjectId = prevProjectIdRef.current;
    prevProjectIdRef.current = projectId;

    // Only reset when the projectId actually changes or on first mount
    if (prevProjectId === projectId) return;

    // Snapshot the navigation state for this project transition.
    // Must happen before any early-return so subsequent effects can
    // read navStateRef.current reliably for this project.
    navStateRef.current = (location.state ?? null) as ChatNavState;

    // Discard stale navigation state on browser refresh.
    // The browser preserves location.state across reloads, but the
    // WebSocket connection and Redux store that the new-project flow
    // depends on are gone. The Navigation Timing API directly reports
    // whether this page load was a reload, so we use it instead of
    // inferring reload status from application state.
    if (navStateRef.current?.isNewProject && isPageReloadRef.current) {
      navStateRef.current = null;
    }

    logger.log({
      loggerMsg: "[IDEChatPanel] Project boundary transition detected",
      from: prevProjectId,
      to: projectId,
    });

    // Clear conversation context so the new project starts with a clean slate.
    // Without this, currentConversationId still points to the previous project's
    // conversation, causing messages from the old project to render.
    clearCurrentConversation();

    // Reset component-local refs
    refs.sessionIdRef.current = null;
    refs.streamingMessageIdRef.current = null;
    refs.processedSegmentsRef.current = 0;

    // Reset Redux Execution state. If the previous project's execution was
    // still in-flight (SENDING/QUEUED/IN_PROGRESS), the streamed responses
    // for the old project will be discarded. This is the correct behavior —
    // the user navigated away, and the new project needs a clean execution slot.
    //
    // However, skip the reset when arriving from HomePage with a new project.
    // HomePage already dispatched startExecution() before navigating here, and
    // resetting would clear executionProjectId — causing ArchitectureComposerPage
    // to miss the completion signal when the backend finishes generating assets.
    if (!navStateRef.current?.isNewProject) {
      dispatch(resetExecution());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, dispatch, clearCurrentConversation]);

  return navStateRef;
}
